// src/pages/AdminCheckinPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import QrScanner from '../components/QrScanner';
import QuickCheckinModal from '../components/admin/QuickCheckinModal';
import CheckinContextSelector from '../components/admin/CheckinContextSelector'; // NOVO IMPORT
import { 
    getPresencasBySessao, 
    getInscricaoByQrCode, 
    registrarPresencaLocal, 
    quickSignupAndCheckin, // ATUALIZADO
    getLocaisSessaoBySessao // Mantido para a lista de presenças
} from '../services/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/Card';
import { ScanLine, User, Users, Mail, BadgeCheck, BadgeX, MapPin, Loader2, UserPlus } from 'lucide-react'; // Adicionado UserPlus
import { Skeleton } from '../components/ui/Skeleton';

function AdminCheckinPage() {
    // Estados de Contexto (passados para CheckinContextSelector)
    const [selectedEventId, setSelectedEventId] = useState('');
    const [selectedSessionId, setSelectedSessionId] = useState('');
    const [selectedLocalSessaoId, setSelectedLocalSessaoId] = useState(''); 
    
    // Estado local para a lista de locais (necessário para exibir o nome na lista de presenças)
    const [localSessoes, setLocalSessoes] = useState([]); 

    // Estados de Check-in
    const [presencas, setPresencas] = useState([]);
    const [loadingPresencas, setLoadingPresencas] = useState(false);
    const [qrCodeInput, setQrCodeInput] = useState('');
    const [participanteDetalhes, setParticipanteDetalhes] = useState(null);
    const [inputStatus, setInputStatus] = useState(''); 
    const [showScanner, setShowScanner] = useState(false);
    const [scannerError, setScannerError] = useState('');
    const [showNotFoundPrompt, setShowNotFoundPrompt] = useState(false);
    const [showQuickCheckinModal, setShowQuickCheckinModal] = useState(false);
    const [notFoundIdentifier, setNotFoundIdentifier] = useState(''); // Usado para preencher o email no modal

    const qrCodeInputRef = useRef(null);

    // Efeito para buscar a lista de locais da sessão (necessário para o resumo de presenças)
    useEffect(() => {
        const fetchLocalSessoesData = async () => {
            if (selectedSessionId) {
                try {
                    const data = await getLocaisSessaoBySessao(selectedSessionId);
                    setLocalSessoes(data);
                } catch (error) {
                    console.error("Erro ao buscar locais para resumo:", error);
                    setLocalSessoes([]);
                }
            } else {
                setLocalSessoes([]);
            }
        };
        fetchLocalSessoesData();
    }, [selectedSessionId]);


    const fetchPresencas = useCallback(async () => {
        if (selectedSessionId) {
            setLoadingPresencas(true);
            try {
                const data = await getPresencasBySessao(selectedSessionId);
                setPresencas(data);
            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoadingPresencas(false);
            }
        } else {
            setPresencas([]);
        }
    }, [selectedSessionId]);

    useEffect(() => {
        fetchPresencas();
    }, [fetchPresencas]);

    // Foca no input quando a sala é selecionada
    useEffect(() => {
        if (selectedLocalSessaoId && qrCodeInputRef.current) { 
            qrCodeInputRef.current.focus();
        }
    }, [selectedLocalSessaoId]);

    const fetchParticipanteDetails = useCallback(async (identifier) => {
        if (!identifier.trim()) {
            setParticipanteDetalhes(null);
            setShowNotFoundPrompt(false);
            return;
        }

        setInputStatus('info');
        try {
            const data = await getInscricaoByQrCode(identifier.trim());
            
            setParticipanteDetalhes(data);
            setInputStatus('info');
            setShowNotFoundPrompt(false);
        } catch (error) {
            console.error('Erro ao buscar detalhes do participante:', error);
            setParticipanteDetalhes(null);
            setInputStatus('error');
            setShowNotFoundPrompt(true);
            setNotFoundIdentifier(identifier.trim());

            if (!error.message.includes('404')) {
                toast.error(error.message || 'Ocorreu um erro ao buscar detalhes do participante.');
            }
            setTimeout(() => setInputStatus(''), 1500);
        }
    }, []); 

    const handleQrCodeInputChange = (e) => {
        const value = e.target.value;
        setQrCodeInput(value);
        setShowNotFoundPrompt(false);
        fetchParticipanteDetails(value);
    };

    const onScanSuccess = (decodedText) => {
        setQrCodeInput(decodedText);
        fetchParticipanteDetails(decodedText);
        setShowScanner(false);
        setScannerError('');
        qrCodeInputRef.current?.focus();
    };

    const onScanError = (errorMessage) => {
        const errorString = String(errorMessage); 
        if (errorString.includes("NotAllowedError") || errorString.includes("NotFoundError")) {
            setScannerError('Permissão de câmera negada ou câmera não encontrada.');
            toast.error('Erro no scanner: Permissão de câmera negada ou câmera não encontrada.');
            setShowScanner(false);
        } else {
            setScannerError(`Erro no scanner: ${errorString}`);
            toast.error(`Erro no scanner: ${errorString}`);
            setShowScanner(false);
        }
    };

    const handleCheckin = async () => {
        if (!selectedLocalSessaoId || !qrCodeInput.trim()) { 
            toast.warn('Selecione uma sala e forneça um QR Code.');
            return;
        }
        setInputStatus('');
        setShowNotFoundPrompt(false);
        
        const checkinToastId = toast.loading("A registar presença...");

        try {
            const data = await registrarPresencaLocal({ 
                codigoQr: qrCodeInput.trim(),
                localSessaoId: parseInt(selectedLocalSessaoId) 
            });
            
            const localNome = localSessoes.find(l => l.id === parseInt(selectedLocalSessaoId))?.nomeCompleto || 'Sala Desconhecida';
            toast.update(checkinToastId, { 
                render: `Check-in de ${data.nomeParticipante} registrado na sala ${localNome}!`, 
                type: "success", 
                isLoading: false, 
                autoClose: 5000 
            });
            
            setQrCodeInput('');
            setParticipanteDetalhes(null);
            setInputStatus('success');
            fetchPresencas();
        } catch (error) {
            console.error('Erro no check-in:', error);
            const errorMessage = error.message || 'Ocorreu um erro desconhecido.';
            
            toast.update(checkinToastId, { 
                render: `Erro: ${errorMessage}`, 
                type: "error", 
                isLoading: false, 
                autoClose: 5000 
            });

            setInputStatus('error');
            setShowNotFoundPrompt(true);
            setNotFoundIdentifier(qrCodeInput.trim());
        } finally {
            qrCodeInputRef.current?.focus();
            setTimeout(() => setInputStatus(''), 2000);
        }
    };

    const handleQuickCheckinOpen = (initialIdentifier = '') => {
        // Se o identificador for um email válido, passamos para o modal
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const initialEmail = emailRegex.test(initialIdentifier) ? initialIdentifier : '';
        
        setNotFoundIdentifier(initialEmail); // Define o email inicial (pode ser vazio)
        setShowQuickCheckinModal(true);
    };

    const handleQuickCheckinSubmit = async (formData) => {
        if (!selectedEventId || !selectedLocalSessaoId) {
            toast.error("Contexto de evento/sala inválido para cadastro rápido.");
            throw new Error("Contexto inválido.");
        }
        
        const quickCheckinToastId = toast.loading("A cadastrar e registar presença...");

        try {
            const payload = {
                ...formData,
                eventoId: parseInt(selectedEventId),
                localSessaoId: parseInt(selectedLocalSessaoId), 
            };
            // USANDO A FUNÇÃO ATUALIZADA
            const novaInscricao = await quickSignupAndCheckin(payload);
            
            const localNome = localSessoes.find(l => l.id === parseInt(selectedLocalSessaoId))?.nomeCompleto || 'Sala Desconhecida';
            toast.update(quickCheckinToastId, {
                render: `Participante ${novaInscricao.nomeParticipante} cadastrado e check-in realizado com sucesso na sala ${localNome}!`,
                type: "success",
                isLoading: false,
                autoClose: 5000
            });
            
            setShowQuickCheckinModal(false);
            setQrCodeInput('');
            setParticipanteDetalhes(null);
            setShowNotFoundPrompt(false);
            fetchPresencas();
        } catch (error) {
            console.error('Erro no cadastro rápido:', error);
            const errorMessage = error.message || 'Ocorreu um erro desconhecido.';
            
            toast.update(quickCheckinToastId, {
                render: `Erro no cadastro rápido: ${errorMessage}`,
                type: "error",
                isLoading: false,
                autoClose: 5000
            });
            throw error; // Re-lança para o modal saber que falhou
        }
    };

    const getInputClassName = () => {
        switch (inputStatus) {
            case 'success': return 'border-green-500 focus-visible:ring-green-500';
            case 'error': return 'border-red-500 focus-visible:ring-red-500';
            case 'info': return 'border-blue-500 focus-visible:ring-blue-500';
            default: return '';
        }
    };

    const PresencasSkeleton = () => (
        <ul className="space-y-3 max-h-[60vh] overflow-y-auto">
            {Array.from({ length: 7 }).map((_, index) => (
                <li key={index} className="flex items-center p-3 bg-muted/50 rounded-md">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="ml-3 flex-grow space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                    </div>
                    <Skeleton className="h-4 w-12" />
                </li>
            ))}
        </ul>
    );

    const currentLocalName = localSessoes.find(l => String(l.id) === selectedLocalSessaoId)?.nomeCompleto;

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <h1 className="text-3xl font-bold tracking-tight mb-6">Check-in de Participantes</h1>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    
                    {/* Passo 1: Seleção de Contexto */}
                    <CheckinContextSelector
                        selectedEventId={selectedEventId}
                        setSelectedEventId={setSelectedEventId}
                        selectedSessionId={selectedSessionId}
                        setSelectedSessionId={setSelectedSessionId}
                        selectedLocalSessaoId={selectedLocalSessaoId}
                        setSelectedLocalSessaoId={setSelectedLocalSessaoId}
                    />

                    {/* Passo 2: Realizar Check-in (Visível apenas se a sala for selecionada) */}
                    {selectedLocalSessaoId ? ( 
                        <Card>
                            <CardHeader>
                                <CardTitle className="mb-2">Passo 2: Realizar Check-in na Sala: {currentLocalName}</CardTitle>
                                <CardDescription className="leading-relaxed">Insira o QR Code do participante, ou use a câmera para escanear.</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-col md:flex-row gap-4">
                                    <div className="flex-grow grid gap-2">
                                        <Label htmlFor="qrCodeInput" className="mb-1">QR Code</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                id="qrCodeInput"
                                                ref={qrCodeInputRef}
                                                placeholder="Insira ou escaneie o código"
                                                value={qrCodeInput}
                                                onChange={handleQrCodeInputChange}
                                                onKeyPress={(e) => e.key === 'Enter' && handleCheckin()}
                                                className={getInputClassName()}
                                            />
                                            <Button variant="outline" size="icon" onClick={() => setShowScanner(!showScanner)}>
                                                <ScanLine className="h-5 w-5" />
                                            </Button>
                                        </div>
                                    </div>
                                    <Button onClick={handleCheckin} className="mt-auto h-10 md:h-auto md:mt-6">Registrar Presença</Button>
                                </div>
                                
                                {showScanner && (
                                    <div className="mt-4 p-4 border rounded-md bg-muted/40">
                                        <QrScanner onScanSuccess={onScanSuccess} onScanError={onScanError} />
                                        {scannerError && <p className="text-red-500 text-sm mt-2">{scannerError}</p>}
                                    </div>
                                )}

                                {/* Detalhes do Participante Encontrado */}
                                {participanteDetalhes && (
                                    <Card className="mt-4 border-blue-500/50 bg-blue-50/50 dark:bg-blue-900/20">
                                        <CardHeader>
                                            <CardTitle className="flex items-center mb-2 text-blue-600 dark:text-blue-400">
                                                <User className="mr-2" /> Detalhes do Participante (Inscrição Encontrada)
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-2 text-sm">
                                            <p><strong>Nome:</strong> {participanteDetalhes.nomeParticipante}</p>
                                            <p><strong>Email:</strong> {participanteDetalhes.emailParticipante}</p>
                                            <p><strong>Evento:</strong> {participanteDetalhes.nomeEvento}</p>
                                            <p className="flex items-center"><strong>Status Inscrição:</strong> 
                                                {participanteDetalhes.status === 'CONFIRMADA' ? 
                                                    <BadgeCheck className="inline-block ml-2 text-green-600" /> : 
                                                    <BadgeX className="inline-block ml-2 text-orange-600" />}
                                                <span className={`ml-1 font-bold ${participanteDetalhes.status === 'CONFIRMADA' ? 'text-green-700 dark:text-green-400' : 'text-orange-700 dark:text-orange-400'}`}>
                                                    {participanteDetalhes.status}
                                                </span>
                                            </p>
                                        </CardContent>
                                    </Card>
                                )}
                            </CardContent>
                            <CardFooter className="flex justify-end border-t pt-4">
                                {/* Botão de Cadastro Rápido - SEMPRE VISÍVEL quando o contexto está ativo */}
                                <Button 
                                    variant="secondary" 
                                    onClick={() => handleQuickCheckinOpen(notFoundIdentifier)} 
                                    className="w-full sm:w-auto"
                                >
                                    <UserPlus className="mr-2 h-4 w-4" /> Cadastro Rápido
                                </Button>
                            </CardFooter>
                        </Card>
                    ) : (
                        <Card className="text-center p-8 border-2 border-dashed rounded-lg bg-secondary/50">
                            <Loader2 className="h-8 w-8 text-primary mx-auto mb-3 animate-spin" />
                            <p className="text-lg font-medium text-muted-foreground">Aguardando Seleção</p>
                            <p className="text-sm text-muted-foreground mt-1">Por favor, selecione o Evento, Sessão e Sala no Passo 1 para iniciar o check-in.</p>
                        </Card>
                    )}
                </div>

                {/* Painel de Presenças */}
                <div className="lg:col-span-1">
                    <Card className="sticky top-24">
                        <CardHeader>
                            <CardTitle className="flex items-center mb-2">
                                <Users className="mr-2" /> Presenças Registradas
                            </CardTitle>
                            <CardDescription className="leading-relaxed">
                                {selectedSessionId ? `Sessão: ${selectedSessionId}` : 'Selecione uma sessão'}
                                {currentLocalName && ` - Sala: ${currentLocalName}`}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loadingPresencas ? (
                                <PresencasSkeleton />
                            ) : presencas.length === 0 ? (
                                <p className="text-muted-foreground text-center py-4">Nenhuma presença registrada ainda.</p>
                            ) : (
                                <ul className="space-y-3 max-h-[60vh] overflow-y-auto">
                                    {presencas.map(p => (
                                        <li key={p.id} className="flex items-center p-3 bg-muted/50 rounded-md">
                                            <div className="flex-shrink-0 bg-primary text-primary-foreground h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold">
                                                {p.nomeParticipante.charAt(0)}
                                            </div>
                                            <div className="ml-3 flex-grow">
                                                <p className="text-sm font-medium text-foreground">{p.nomeParticipante}</p>
                                                <p className="text-xs text-muted-foreground flex items-center">
                                                    <Mail className="mr-1.5 h-3 w-3" /> {p.emailParticipante}
                                                </p>
                                            </div>
                                            <div className="text-xs text-muted-foreground text-right">
                                                {new Date(p.dataHoraRegistro).toLocaleTimeString('pt-BR')}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
            <QuickCheckinModal
                isOpen={showQuickCheckinModal}
                onClose={() => setShowQuickCheckinModal(false)}
                onSubmit={handleQuickCheckinSubmit}
                eventoId={selectedEventId}
                sessaoId={selectedSessionId} 
                localSessaoId={selectedLocalSessaoId} 
                initialEmail={notFoundIdentifier}
            />
        </div>
    );
}

export default AdminCheckinPage;